import { wildResponseHandler } from "@wildidea/tools/utils/wildResponseHandler.js";

import { IUsersServiceClient } from "../../../../../src/auth/domain/IUsersService.js";
import {
  API_PREFIX,
  CreateUserInput,
  FollowRequestInput,
  GroupSummary,
  ResolvedInput,
  UpdateUserPasswordInput,
  UpdateUserPrivacyInput,
  UpdateUserProfileAttributesInput,
  User,
  UserMapShareLink,
  UserSignInAttributes,
  UserSummary,
  WithUserId,
} from "../model.js";

const JSON_RESPONSE_HEADERS = { Accept: "application/json" };
const JSON_REQUEST_HEADERS = { "Content-Type": "application/json" };

export interface UsersServiceClientConfig {
  /**
   * A string to prepend to every API call, e.g. `https://dev.onemap.earth`.
   *
   * Not necessary if you are running in a web app. The calls will be made to a relative path
   * from `/api/` in the current domain, which should map directly to the same the API paths in
   * API Gateway.
   */
  baseUri?: string;
}

export class UsersServiceClient implements IUsersServiceClient {
  private readonly baseUri: string;
  protected baseHeaders: Record<string, string> = JSON_RESPONSE_HEADERS;
  constructor(config: UsersServiceClientConfig) {
    this.baseUri = config.baseUri || "";
  }

  async createUser(input: CreateUserInput): Promise<User> {
    return wildResponseHandler(
      fetch(`${this.baseUri}${API_PREFIX}/users`, {
        method: "POST",
        headers: { ...this.baseHeaders, ...JSON_REQUEST_HEADERS },
        body: JSON.stringify(input),
      }),
      "createUser",
    );
  }

  private endpoint_userProfile(userId: string) {
    return `${this.baseUri}${API_PREFIX}/users/${userId}/profile`;
  }

  async getUserProfile(input: WithUserId): Promise<UserSummary> {
    return wildResponseHandler(
      fetch(this.endpoint_userProfile(input.userId), {
        headers: this.baseHeaders,
      }),
      "getUserProfile",
    );
  }

  async updateUserProfileAttributes({
    userId,
    ...body
  }: UpdateUserProfileAttributesInput): Promise<void> {
    return wildResponseHandler(
      fetch(this.endpoint_userProfile(userId), {
        method: "POST",
        headers: { ...this.baseHeaders, ...JSON_REQUEST_HEADERS },
        body: JSON.stringify(body),
      }),
      "updateUserProfileAttributes",
    );
  }

  private endpoint_userSignInAttributes(userId: string) {
    return `${this.baseUri}${API_PREFIX}/users/${userId}/account`;
  }

  async getUserSignInAttributes(
    input: WithUserId,
  ): Promise<UserSignInAttributes> {
    return wildResponseHandler(
      fetch(this.endpoint_userSignInAttributes(input.userId), {
        headers: this.baseHeaders,
      }),
      "getUserSignInAttributes",
    );
  }

  async updateUserSignInAttributes({
    userId,
    ...body
  }: UserSignInAttributes): Promise<void> {
    return wildResponseHandler(
      fetch(this.endpoint_userSignInAttributes(userId), {
        method: "POST",
        headers: { ...this.baseHeaders, ...JSON_REQUEST_HEADERS },
        body: JSON.stringify(body),
      }),
      "updateUserSignInAttributes",
    );
  }

  async updateUserPassword({
    userId,
    ...body
  }: UpdateUserPasswordInput): Promise<void> {
    return wildResponseHandler(
      fetch(`${this.baseUri}${API_PREFIX}/users/${userId}/password`, {
        method: "POST",
        headers: { ...this.baseHeaders, ...JSON_REQUEST_HEADERS },
        body: JSON.stringify(body),
      }),
      "updateUserPassword",
    );
  }

  async updateUserPrivacy({
    userId,
    ...body
  }: UpdateUserPrivacyInput): Promise<void> {
    return wildResponseHandler(
      fetch(`${this.baseUri}${API_PREFIX}/users/${userId}/privacy`, {
        method: "POST",
        headers: { ...this.baseHeaders, ...JSON_REQUEST_HEADERS },
        body: JSON.stringify(body),
      }),
      "updateUserPrivacy",
    );
  }

  async updateUserMapShareLink({
    userId,
    ...body
  }: UserMapShareLink & WithUserId): Promise<void> {
    return wildResponseHandler(
      fetch(`${this.baseUri}${API_PREFIX}/users/${userId}/mapshare`, {
        method: "POST",
        headers: { ...this.baseHeaders, ...JSON_REQUEST_HEADERS },
        body: JSON.stringify(body),
      }),
      "updateUserMapShareLink",
    );
  }

  private endpoint_follow({ followerId, followingId }: FollowRequestInput) {
    return `${this.baseUri}${API_PREFIX}/users/${followingId}/followers/${followerId}`;
  }

  async createFollowRequest({
    followerId,
    followingId,
  }: FollowRequestInput): Promise<void> {
    await wildResponseHandler(
      fetch(this.endpoint_follow({ followerId, followingId }), {
        method: "POST",
        headers: this.baseHeaders,
      }),
      "createFollowRequest",
    );
  }

  async resolveFollowRequest({
    followerId,
    followingId,
    ...body
  }: FollowRequestInput & ResolvedInput): Promise<void> {
    await wildResponseHandler(
      fetch(`${this.endpoint_follow({ followerId, followingId })}/resolve`, {
        method: "POST",
        headers: { ...this.baseHeaders, ...JSON_REQUEST_HEADERS },
        body: JSON.stringify(body),
      }),
      "resolveFollowRequest",
    );
  }

  async removeFollow({
    followerId,
    followingId,
  }: FollowRequestInput): Promise<void> {
    return wildResponseHandler(
      fetch(this.endpoint_follow({ followerId, followingId }), {
        method: "DELETE",
        headers: this.baseHeaders,
      }),
      "removeFollow",
    );
  }

  async getPendingFollowRequests(input: WithUserId): Promise<UserSummary[]> {
    return wildResponseHandler(
      fetch(
        `${this.baseUri}${API_PREFIX}/users/${input.userId}/pending-followers`,
        {
          headers: this.baseHeaders,
        },
      ),
      "getPendingFollowRequests",
    );
  }

  async getFollowers(input: WithUserId): Promise<UserSummary[]> {
    return wildResponseHandler(
      fetch(`${this.baseUri}${API_PREFIX}/users/${input.userId}/followers`, {
        headers: this.baseHeaders,
      }),
      "getFollowers",
    );
  }

  async getFollows(input: WithUserId): Promise<UserSummary[]> {
    return wildResponseHandler(
      fetch(`${this.baseUri}${API_PREFIX}/users/${input.userId}/follows`, {
        headers: this.baseHeaders,
      }),
      "getFollows",
    );
  }

  async getUserGroups(input: WithUserId): Promise<GroupSummary[]> {
    return wildResponseHandler(
      fetch(`${this.baseUri}${API_PREFIX}/users/${input.userId}/groups`, {
        headers: this.baseHeaders,
      }),
      "getUserGroups",
    );
  }
}
